'use client';

import { useProject } from '@/features/project/hooks/use-projects';
import type { ProjectFeatureFlags } from '@/types/project';
import { useSubscriptionLimits, type SubscriptionLimits } from './subscription.selectors';

/**
 * Subset of {@link SubscriptionLimits} that is effective PER BUILDING — a
 * Facade Pack (or Builder Pass slot) on a project flips these to `true` for
 * that building even when the user's base plan lacks them. These are exactly
 * the feature toggles surfaced on render/album settings.
 */
export interface ProjectFeatures {
  has_hires_render: boolean;
  has_extended_thinking: boolean;
  has_model_selection: boolean;
  has_output_style: boolean;
  has_cost_estimate: boolean;
  has_high_effort: boolean;
  has_remove_watermark: boolean;
  has_upscale: boolean;
  is_white_label_allowed: boolean;
  is_album_advanced_options_available: boolean;
  is_album_templates_available: boolean;
  /**
   * Manual "engine" knobs (Effort, Model, Extended thinking, Hi-Res) are shown
   * ONLY to advanced-control holders (Builder Pass, grandfathered Pro/Designer).
   * Free + one-time Facade Pack buyers don't see them at all — the server
   * auto-resolves effort/model and the client omits those keys from the render
   * request. Same naming convention as `is_white_label_allowed`.
   */
  has_advanced_render_controls: boolean;
}

/** Map the server `ProjectResource.attributes.features` block to our flags. */
function fromProjectFlags(f: ProjectFeatureFlags): ProjectFeatures {
  return {
    has_hires_render: f.is_feature_hires_render,
    has_extended_thinking: f.is_feature_extended_thinking,
    has_model_selection: f.is_feature_model_selection,
    has_output_style: f.is_feature_output_style,
    has_cost_estimate: f.is_feature_cost_estimate,
    has_high_effort: f.is_feature_high_effort,
    has_remove_watermark: f.is_feature_remove_watermark,
    has_upscale: f.is_feature_upscale,
    is_white_label_allowed: f.is_white_label_allowed,
    is_album_advanced_options_available: f.is_album_advanced_options_available,
    is_album_templates_available: f.is_album_templates_available,
    has_advanced_render_controls: f.is_advanced_render_controls,
  };
}

/** Map the user-level plan limits to the same flag shape (fallback path). */
function fromPlanLimits(limits: SubscriptionLimits | null): ProjectFeatures {
  return {
    has_hires_render: limits?.has_hires_render ?? false,
    has_extended_thinking: limits?.has_extended_thinking ?? false,
    has_model_selection: limits?.has_model_selection ?? false,
    has_output_style: limits?.has_output_style ?? false,
    has_cost_estimate: limits?.has_cost_estimate ?? false,
    // Plan limits don't carry high_effort; without project context the toggle
    // falls back to the user plan, so default `false` here (the server still
    // enforces the legacy-token exemption at render time — and that exemption
    // surfaces via the project `features` block when building context exists).
    has_high_effort: false,
    has_remove_watermark: limits?.has_remove_watermark ?? false,
    has_upscale: limits?.has_upscale ?? false,
    is_white_label_allowed: limits?.is_white_label_allowed ?? false,
    is_album_advanced_options_available: false,
    is_album_templates_available: false,
    has_advanced_render_controls: limits?.has_advanced_render_controls ?? false,
  };
}

/**
 * Effective feature flags for a render/album surface, scoped to its building.
 *
 * Carry-over fix (Facade Pack pivot): feature toggles (Hi-Res, extended
 * thinking, model picker, output style, album advanced options, high effort)
 * must gate on the per-building EFFECTIVE flags — a pack-active building grants
 * the pro-creative set even on a free/lite base plan. When a `projectId` is
 * provided and the cached project carries the server `features` block, we read
 * it; otherwise we fall back to the user-level plan limits (no building scope).
 *
 * The locked PRO/DESIGNER badge must therefore key off the returned flag, not
 * the user plan — so a pack building shows no lock.
 */
export function useProjectFeatures(projectId?: string): ProjectFeatures {
  const limits = useSubscriptionLimits();
  const { data: projectResponse } = useProject(projectId ?? '', {
    enabled: !!projectId,
  });
  const features = projectResponse?.data?.attributes?.features;
  if (projectId && features) {
    return fromProjectFlags(features);
  }
  return fromPlanLimits(limits);
}
